import { findParentNodeClosestToPos, mergeAttributes, Node } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import { isAllowedEmbedSrc, isHttpsUrl, isSafeEmbedPoster } from '../utils/embeds';

// Alignment values map to CSS classes on the <figure> element. "left" and
// "right" float the figure so paragraph text wraps around it; "center" and
// "full" are block layouts.
export const FIGURE_ALIGNMENTS = ['center', 'left', 'right', 'full'];

// Width presets offered by the media controls, as percentages of the text
// column. Floated figures are capped at 50% by clampWidth so text always has
// room to wrap beside them.
export const FIGURE_WIDTHS = [25, 40, 50, 75, 100];

// The kinds of media a figure can wrap. Every kind gets the same layout
// controls (alignment, width, caption, accessible label). 'embed' is a
// third-party player iframe (YouTube/Rumble), allowlisted in utils/embeds.
export const FIGURE_MEDIA_TYPES = ['image', 'video', 'audio', 'embed'];

const MEDIA_TAG_TO_TYPE = { img: 'image', video: 'video', audio: 'audio', iframe: 'embed' };

const clampWidth = (width, align) => {
    if (align === 'full') {
        return null;
    }

    const parsed = Number.parseInt(width, 10);
    if (!Number.isFinite(parsed)) {
        return null;
    }
    const max = (align === 'left' || align === 'right') ? 50 : 100;
    return Math.min(Math.max(parsed, 15), max);
};

// Only http(s), protocol-relative, or scheme-less (relative) URLs may become
// media sources. Pasted data:/javascript:/other schemes are dropped so
// external HTML can't smuggle payloads past the upload pipeline the way the
// Image extension already prevents (allowBase64: false).
export const isSafeMediaSrc = (src) => {
    if (typeof src !== 'string' || !src.trim()) {
        return false;
    }
    const trimmed = src.trim();
    if (/^(https?:)?\/\//i.test(trimmed)) {
        return true;
    }
    return !/^[a-z][a-z0-9+.-]*:/i.test(trimmed);
};

const findMediaElement = (element) => element.querySelector('img, video, audio, iframe');

// Embeds get the strict origin allowlist; everything else the scheme gate.
const isUsableMediaSrc = (media, src) => (
    media?.tagName?.toLowerCase() === 'iframe'
        ? isAllowedEmbedSrc(src)
        : isSafeMediaSrc(src)
);

// src lives on the media element itself, or on a <source> child in pasted
// <video><source src></video> markup.
const mediaElementSrc = (media) => {
    if (!media) {
        return null;
    }
    const direct = media.getAttribute('src');
    if (direct) {
        return direct;
    }
    const source = media.querySelector('source[src]');
    return source ? source.getAttribute('src') : null;
};

// The still shown before playback: a video's poster attribute, or an
// embed's data-poster. Null when absent or unsafe.
const mediaElementPoster = (media) => {
    const tag = media?.tagName?.toLowerCase();
    if (tag === 'video') {
        const poster = media.getAttribute('poster');
        return isSafeMediaSrc(poster) ? poster : null;
    }
    if (tag === 'iframe') {
        const poster = media.getAttribute('data-poster');
        return isSafeEmbedPoster(poster) ? poster : null;
    }
    return null;
};

// Renders the media element for a figure or standalone media node as a
// prosemirror DOM spec. Video and audio always get native controls; the
// accessible label rides on aria-label since only <img> has alt. A poster
// (video: poster, embed: data-poster) is emitted only when set, so markup
// without one stays byte-identical.
const renderMediaElement = (mediaType, { src, alt, poster }, extraAttributes = {}) => {
    if (mediaType === 'video') {
        return ['video', {
            src,
            ...(poster ? { poster } : {}),
            controls: 'controls',
            preload: 'metadata',
            playsinline: 'playsinline',
            ...(alt ? { 'aria-label': alt } : {}),
            ...extraAttributes,
        }];
    }

    if (mediaType === 'audio') {
        return ['audio', {
            src,
            controls: 'controls',
            preload: 'metadata',
            ...(alt ? { 'aria-label': alt } : {}),
            ...extraAttributes,
        }];
    }

    if (mediaType === 'embed') {
        return ['iframe', {
            src,
            ...(poster ? { 'data-poster': poster } : {}),
            frameborder: '0',
            allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
            allowfullscreen: 'allowfullscreen',
            loading: 'lazy',
            referrerpolicy: 'strict-origin-when-cross-origin',
            ...(alt ? { title: alt } : {}),
            ...extraAttributes,
        }];
    }

    return ['img', { src, ...(alt ? { alt } : {}), ...extraAttributes }];
};

// The stock Image parse rule only excludes data: srcs; reject javascript:
// and every other non-http(s)/relative scheme too, so standalone images get
// the same gate as figures and media nodes.
export const SafeImage = Image.extend({
    parseHTML() {
        return this.parent().map((rule) => ({
            ...rule,
            getAttrs: (element) => (
                isSafeMediaSrc(element.getAttribute('src')) ? null : false
            ),
        }));
    },
});

// Locates the figure node containing (or selected by) the current selection.
export const findFigure = (state) => {
    const { selection } = state;

    if (selection.node?.type?.name === 'figure') {
        return { node: selection.node, pos: selection.from };
    }

    const parent = findParentNodeClosestToPos(
        state.doc.resolve(selection.from),
        (node) => node.type.name === 'figure'
    );

    return parent ? { node: parent.node, pos: parent.pos } : null;
};

// A caption-less block video or audio player — the media counterpart of the
// plain Image node, so the caption toggle can unwrap a figure for every
// media kind. Attribute-driven and atomic: the player can't be edited as
// text, only selected, dragged, or re-wrapped into a figure.
export const Media = Node.create({
    name: 'media',

    group: 'block',

    atom: true,

    draggable: true,

    addAttributes() {
        return {
            mediaType: {
                default: 'video',
            },
            src: {
                default: null,
            },
            alt: {
                default: null,
            },
            // Still shown before playback (video and embed only). Parsed per
            // attribute — tiptap merges these over the rules' getAttrs, and
            // the default would copy the raw, unchecked attribute.
            poster: {
                default: null,
                parseHTML: (element) => mediaElementPoster(element),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'video',
                getAttrs: (element) => {
                    const src = mediaElementSrc(element);
                    return isSafeMediaSrc(src)
                        ? { mediaType: 'video', src, alt: element.getAttribute('aria-label') }
                        : false;
                },
            },
            {
                tag: 'audio',
                getAttrs: (element) => {
                    const src = mediaElementSrc(element);
                    return isSafeMediaSrc(src)
                        ? { mediaType: 'audio', src, alt: element.getAttribute('aria-label') }
                        : false;
                },
            },
            {
                tag: 'iframe',
                getAttrs: (element) => {
                    const src = element.getAttribute('src');
                    return isAllowedEmbedSrc(src)
                        ? { mediaType: 'embed', src, alt: element.getAttribute('title') }
                        : false;
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const { mediaType, src, alt, poster, ...rest } = HTMLAttributes;
        const safeType = (mediaType === 'audio' || mediaType === 'embed') ? mediaType : 'video';
        return renderMediaElement(safeType, { src, alt, poster }, mergeAttributes(rest, {
            class: 'rich-text-editor__media',
        }));
    },
});

// A block figure: a media element (image, video, or audio) with an editable
// caption underneath. The node's inline content is rendered inside
// <figcaption>; the media element itself is attribute-driven so it can't be
// edited as text. Alignment ("left"/"right" floats with text wrap, "center",
// "full") and width (% of the text column) are stored as attributes and
// rendered as classes plus a --rte-figure-width custom property that the
// editor, site, and legacy stylesheets all read.
//
// The figure is itself a textblock (content 'inline*'), so block-changing
// commands could convert it and destroy the media; the keymap below plus the
// toolbar guards in RichTextEditor.js intercept those inside captions. The
// high priority puts this extension's keymap ahead of StarterKit's.
const Figure = Node.create({
    name: 'figure',

    priority: 1000,

    group: 'block',

    content: 'inline*',

    isolating: true,

    draggable: true,

    addAttributes() {
        return {
            mediaType: {
                default: 'image',
                parseHTML: (element) => {
                    const media = findMediaElement(element);
                    const tag = media?.tagName?.toLowerCase();
                    return MEDIA_TAG_TO_TYPE[tag] || 'image';
                },
            },
            src: {
                default: null,
                parseHTML: (element) => {
                    const media = findMediaElement(element);
                    const src = mediaElementSrc(media);
                    return isUsableMediaSrc(media, src) ? src : null;
                },
            },
            alt: {
                default: null,
                parseHTML: (element) => {
                    const media = findMediaElement(element);
                    return media?.getAttribute('alt')
                        || media?.getAttribute('aria-label')
                        || media?.getAttribute('title')
                        || null;
                },
            },
            poster: {
                default: null,
                parseHTML: (element) => mediaElementPoster(findMediaElement(element)),
            },
            align: {
                default: 'center',
                parseHTML: (element) => {
                    const match = FIGURE_ALIGNMENTS.find(
                        (value) => element.classList.contains(`rte-figure--${value}`)
                    );
                    return match || 'center';
                },
            },
            width: {
                default: null,
                parseHTML: (element) => {
                    const raw = element.style.getPropertyValue('--rte-figure-width');
                    return raw ? Number.parseInt(raw, 10) || null : null;
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'figure',
                // Only claim figures that wrap a media element; pull quotes,
                // embeds, and tables fall through so their real content is
                // parsed by the generic rules instead of being gutted into a
                // broken <img>.
                getAttrs: (element) => {
                    const media = findMediaElement(element);
                    if (!media) {
                        return false;
                    }
                    // A figure around a non-allowlisted iframe falls through
                    // instead of becoming a src-less broken embed.
                    if (media.tagName.toLowerCase() === 'iframe'
                        && !isAllowedEmbedSrc(mediaElementSrc(media))) {
                        return false;
                    }
                    return null;
                },
                // A figure may legally omit its <figcaption>; fall back to a
                // detached empty element — a bare string selector would
                // resolve to null and crash prosemirror's DOM parser
                // (addAll(null) dereferences firstChild).
                contentElement: (element) => element.querySelector('figcaption')
                    || element.ownerDocument.createElement('figcaption'),
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const { mediaType, src, alt, poster, align, width, ...rest } = HTMLAttributes;
        const safeType = FIGURE_MEDIA_TYPES.includes(mediaType) ? mediaType : 'image';
        const safeAlign = FIGURE_ALIGNMENTS.includes(align) ? align : 'center';
        const safeWidth = clampWidth(width, safeAlign);

        return [
            'figure',
            mergeAttributes(rest, {
                class: `rte-figure rte-figure--${safeAlign}`,
                ...(safeWidth ? { style: `--rte-figure-width: ${safeWidth}%` } : {}),
            }),
            renderMediaElement(safeType, { src, alt, poster }, {
                draggable: 'false',
                contenteditable: 'false',
            }),
            ['figcaption', 0],
        ];
    },

    addCommands() {
        return {
            // Inserts a new figure. When the selection sits inside an existing
            // figure (e.g. an empty caption), insert after that figure —
            // inserting at the caret would trigger prosemirror's
            // empty-textblock expansion and REPLACE the previous figure,
            // which silently dropped all but the last file of a multi-upload.
            setFigure: ({ caption, ...attrs }) => ({ state, chain }) => {
                const node = {
                    type: this.name,
                    attrs,
                    content: caption ? [{ type: 'text', text: caption }] : [],
                };

                const figure = findFigure(state);
                if (figure) {
                    return chain()
                        .insertContentAt(figure.pos + figure.node.nodeSize, node)
                        .run();
                }

                // A selected standalone player/image: insert after it —
                // insertContent would replace the NodeSelection and silently
                // destroy the selected node.
                const { selection } = state;
                const selectedName = selection.node?.type?.name;
                if (selectedName === 'image' || selectedName === 'media') {
                    return chain().insertContentAt(selection.to, node).run();
                }

                return chain().insertContent(node).run();
            },

            // Wrap the selected plain media node (image or media) in a figure
            // so a caption can be written under it.
            mediaToFigure: () => ({ state, chain }) => {
                const { selection } = state;
                const node = selection.node;
                if (!node) {
                    return false;
                }

                let attrs = null;
                if (node.type.name === 'image') {
                    attrs = { mediaType: 'image', src: node.attrs.src, alt: node.attrs.alt || null };
                } else if (node.type.name === 'media') {
                    attrs = {
                        mediaType: node.attrs.mediaType,
                        src: node.attrs.src,
                        alt: node.attrs.alt || null,
                        poster: node.attrs.poster || null,
                    };
                } else {
                    return false;
                }

                return chain()
                    .insertContentAt(
                        { from: selection.from, to: selection.to },
                        { type: this.name, attrs }
                    )
                    .setNodeSelection(selection.from)
                    .run();
            },

            // Unwrap a figure back to a plain media node, dropping the caption.
            figureToMedia: () => ({ state, chain }) => {
                const figure = findFigure(state);
                if (!figure) {
                    return false;
                }

                const { mediaType, src, alt, poster } = figure.node.attrs;
                const target = (mediaType && mediaType !== 'image')
                    ? { type: 'media', attrs: { mediaType, src, alt: alt || null, poster: poster || null } }
                    : { type: 'image', attrs: { src, alt: alt || null } };

                return chain()
                    .insertContentAt(
                        { from: figure.pos, to: figure.pos + figure.node.nodeSize },
                        target
                    )
                    .setNodeSelection(figure.pos)
                    .run();
            },

            // Updates align/width/alt on the current figure. A selected plain
            // media node is converted to a figure first so layout controls
            // "just work" on either node type.
            applyFigure: (attrs) => ({ state, chain }) => {
                const { selection } = state;
                const selectedName = selection.node?.type?.name;

                if (selectedName === 'image' || selectedName === 'media') {
                    const align = FIGURE_ALIGNMENTS.includes(attrs.align) ? attrs.align : 'center';
                    return chain()
                        .insertContentAt(
                            { from: selection.from, to: selection.to },
                            {
                                type: this.name,
                                attrs: {
                                    mediaType: selectedName === 'media'
                                        ? selection.node.attrs.mediaType
                                        : 'image',
                                    src: selection.node.attrs.src,
                                    alt: attrs.alt !== undefined
                                        ? (attrs.alt || null)
                                        : (selection.node.attrs.alt || null),
                                    poster: selectedName === 'media'
                                        ? (selection.node.attrs.poster || null)
                                        : null,
                                    align,
                                    width: clampWidth(attrs.width, align),
                                },
                            }
                        )
                        .setNodeSelection(selection.from)
                        .run();
                }

                const figure = findFigure(state);
                if (!figure) {
                    return false;
                }

                const align = attrs.align !== undefined ? attrs.align : figure.node.attrs.align;
                const width = clampWidth(
                    attrs.width !== undefined ? attrs.width : figure.node.attrs.width,
                    align
                );

                return chain()
                    .command(({ tr }) => {
                        tr.setNodeMarkup(figure.pos, undefined, {
                            ...figure.node.attrs,
                            ...attrs,
                            align,
                            width,
                        });
                        return true;
                    })
                    .run();
            },

            // Sets (or with a falsy poster, removes) the poster on every
            // video or embed — figure or standalone player — playing `src`.
            // Keyed by src rather than the selection because the poster
            // upload is async and the writer may have moved on meanwhile.
            // addToHistory: false keeps a poster the editor set on its own
            // (the frame captured after upload) out of the undo stack, so
            // Ctrl+Z goes on undoing the writer's edits instead. An embed
            // only takes a poster that passes isSafeEmbedPoster (the gate
            // parseHTML and the public facade apply), so the editor never
            // shows one that a reload would silently drop.
            setVideoPoster: ({ src, poster, addToHistory = true }) => ({ tr, state, dispatch }) => {
                if (!src) {
                    return false;
                }
                if (dispatch && !addToHistory) {
                    tr.setMeta('addToHistory', false);
                }

                let found = false;
                state.doc.descendants((node, pos) => {
                    const isVideo = (node.type.name === this.name || node.type.name === 'media')
                        && (node.attrs.mediaType === 'video'
                            || (node.attrs.mediaType === 'embed' && (!poster || isSafeEmbedPoster(poster))))
                        && node.attrs.src === src;
                    if (isVideo) {
                        found = true;
                        if (dispatch) {
                            tr.setNodeMarkup(pos, undefined, { ...node.attrs, poster: poster || null });
                        }
                    }
                });
                return found;
            },

            // Fills in what Rumble's oEmbed reported for an embed link — its
            // thumbnail as the poster (https only) and its title as the
            // accessible label — on every embed playing `src`, leaving
            // anything the writer already set alone. Keyed by src like
            // setVideoPoster: the lookup finishes after the insert.
            fillEmbedDetails: ({ src, poster, title, addToHistory = true }) => ({ tr, state, dispatch }) => {
                const safePoster = isHttpsUrl(poster) ? poster.trim() : null;
                const safeTitle = typeof title === 'string' && title.trim() ? title.trim() : null;
                if (!src || (!safePoster && !safeTitle)) {
                    return false;
                }

                let changed = false;
                state.doc.descendants((node, pos) => {
                    const isEmbed = (node.type.name === this.name || node.type.name === 'media')
                        && node.attrs.mediaType === 'embed'
                        && node.attrs.src === src;
                    if (!isEmbed) {
                        return;
                    }
                    const nextPoster = node.attrs.poster || safePoster;
                    const nextAlt = node.attrs.alt || safeTitle;
                    if (nextPoster !== node.attrs.poster || nextAlt !== node.attrs.alt) {
                        changed = true;
                        if (dispatch) {
                            tr.setNodeMarkup(pos, undefined, { ...node.attrs, poster: nextPoster, alt: nextAlt });
                        }
                    }
                });
                if (changed && dispatch && !addToHistory) {
                    tr.setMeta('addToHistory', false);
                }
                return changed;
            },

            deleteFigure: () => ({ state, chain }) => {
                const figure = findFigure(state);
                if (!figure) {
                    return false;
                }

                return chain()
                    .command(({ tr }) => {
                        tr.delete(figure.pos, figure.pos + figure.node.nodeSize);
                        return true;
                    })
                    .run();
            },
        };
    },

    addKeyboardShortcuts() {
        // Swallow block-conversion shortcuts inside a caption: applied to the
        // figure textblock they would convert the node and destroy the media
        // (heading/code block) or wrap it away (lists/quote).
        const blockInsideFigure = () => findFigure(this.editor.state) !== null;

        return {
            // Enter inside a caption (or on a selected figure) moves on to a
            // fresh paragraph after the figure instead of splitting it.
            Enter: () => {
                const figure = findFigure(this.editor.state);
                if (!figure) {
                    return false;
                }

                const after = figure.pos + figure.node.nodeSize;
                return this.editor
                    .chain()
                    .insertContentAt(after, { type: 'paragraph' })
                    .focus(after + 1)
                    .run();
            },
            'Mod-Alt-1': blockInsideFigure,
            'Mod-Alt-2': blockInsideFigure,
            'Mod-Alt-3': blockInsideFigure,
            'Mod-Alt-4': blockInsideFigure,
            'Mod-Alt-5': blockInsideFigure,
            'Mod-Alt-6': blockInsideFigure,
            'Mod-Alt-c': blockInsideFigure,
            'Mod-Shift-7': blockInsideFigure,
            'Mod-Shift-8': blockInsideFigure,
            'Mod-Shift-b': blockInsideFigure,
        };
    },
});

export default Figure;
